import type { IconType } from "../../icon";
import { useAppTheme } from "../../useAppTheme";
import { AppIcon } from "../AppIcon";
import DonutScore from "../DonutScore";

type RingMetrics = {
	radius: number;
	innerRadius: number;
	iconSize: number;
};

/**
 * Exact metrics for the two sizes this ring is drawn at today. Any other
 * `size` falls back to a proportional approximation.
 */
const RING_METRICS: Record<number, RingMetrics> = {
	40: { radius: 20, innerRadius: 16, iconSize: 20 },
	48: { radius: 24, innerRadius: 19, iconSize: 20 },
};

const getRingMetrics = (size: number): RingMetrics =>
	RING_METRICS[size] ?? {
		radius: size / 2,
		innerRadius: size / 2 - 4,
		iconSize: Math.round(size / 2),
	};

export type CompletionRingProps = {
	progress: number;
	icon?: IconType;
	size?: number;
};

export const CompletionRing = ({
	progress,
	icon,
	size = 40,
}: CompletionRingProps) => {
	const theme = useAppTheme();
	const isComplete = progress >= 100;
	const { radius, innerRadius, iconSize } = getRingMetrics(size);

	const renderCenter = () => {
		if (isComplete) {
			return (
				<AppIcon
					name="check-bold"
					size={iconSize}
					color={theme.colors.primary}
				/>
			);
		}
		if (icon) {
			return (
				<AppIcon
					name={icon}
					size={iconSize}
					color={theme.colors.onSurfaceVariant}
				/>
			);
		}
		// Empty fragment to avoid DonutScore's default "N%" fallback text.
		return <></>;
	};

	return (
		<DonutScore
			score={progress}
			distribution={[progress, 100 - progress]}
			variant="progress"
			radius={radius}
			innerRadius={innerRadius}
			customColorScale={[theme.colors.primary, theme.palette.progress.none]}
			customChildren={renderCenter()}
		/>
	);
};
